import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { emptyModal } from 'src/app/modal/DAO';
import { Endorsement, EndorsementQuestionGroup, EndorsementTypes, PortalStatus } from 'src/app/modal/Endorsements/Endorsement.Model';
import { IBasePage } from 'src/app/modal/IBasePage.modules';
import { Constants } from 'src/app/modal/ProjConstants.models';
import { QuestionAnswer, Questionnaire } from 'src/app/modal/questionnaire.models';
import { MenuClickContext } from 'src/app/modal/routing.models';
import { CommonService } from 'src/app/services/commonService';
import { EndorsementService } from 'src/app/services/endorsement.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FocusTrapModule } from 'primeng/focustrap';
import { AutoFocusModule } from 'primeng/autofocus';
import { FileUploadFormComponent } from '../file-upload-form/file-upload-form.component';
import { EndorsementModule } from '../endorsement.module';
import _ from 'lodash';

type LossRunForm = FormGroup<{
  notes: FormControl<string | null>;
}>;

@Component({
  selector: 'loss-run-request',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FocusTrapModule,
    AutoFocusModule,
    EndorsementModule
  ],
  templateUrl: './loss-run-request.component.html'
})
export class LossRunRequestComponent implements OnInit, OnDestroy, IBasePage {

  @ViewChild('fileUploadForm') fileUploadForm: FileUploadFormComponent;

  private formBuilder = inject(FormBuilder);
  private messageService = inject(MessageService);
  private commonService = inject(CommonService);
  public router = inject(Router);
  private endorsementService = inject(EndorsementService);

  public endorsement: Endorsement = this.endorsementService.currentEndorsement;
  public endorsementQuestionGroup: EndorsementQuestionGroup;

  public lossRunForm: LossRunForm | null = null;
  public lossRunFormDataModel: LossRunFormDataModel = new LossRunFormDataModel();
  private initialFormValue: any;

  private menuClickSubscription: Subscription;
  private menuClickContext: MenuClickContext;
  private readonly toastValidation: string = 'Please fill the required fields highlighted in red';
  public fileNetAllowedFileExtensions = Constants.FileNetAllowedFileExtensions.toString();
  public isSubmitted: boolean = false;

  ngOnInit(): void {
    this.initializeData();
    this.menuClickSubscription = this.endorsementService.menuClickBehavior.subscribe(
      (menuClickContext: MenuClickContext) => { this.menuClicked(menuClickContext) }
    );
  }

  private async getQuestionAnswers(): Promise<Questionnaire> {
    let payload = { "urlData": [this.endorsement.endorsementId, EndorsementTypes.LossRun] };
    return this.commonService.get('endorsement', 'getQuestionAnswers', payload).toPromise()
      .then(questionAnswer => {
        const questionnaire: Questionnaire = new Questionnaire();
        questionnaire.key = EndorsementTypes.LossRun;
        questionnaire.questionAnswer = questionAnswer
        return questionnaire;
      })
      .catch(error => Promise.reject(error));
  }

  public async initializeData(): Promise<void> {
    await this.endorsementService.reloadQuestionGroups(this.endorsement);
    this.endorsementQuestionGroup = this.endorsement.selectedEndorsementGroups.find(x => x.groupKey == EndorsementTypes.LossRun);
    this.getQuestionAnswers().then(async questionnaire => {
      if (!!questionnaire.questionAnswer && questionnaire.questionAnswer.length > 0) {
        this.endorsementQuestionGroup.questionAnswers = questionnaire.questionAnswer;
        this.lossRunFormDataModel = LossRunFormDataModel.instantiateFrom(questionnaire);
        this.lossRunForm = this.buildReactiveForm(this.lossRunFormDataModel);
        this.initialFormValue = _.cloneDeep(this.lossRunForm.getRawValue());
        this.subscribeToFormChanges();
      }
    }).catch(error => {
      console.error(error);
    });
  }

  private buildReactiveForm(lossRunRequestFormDataModel: LossRunFormDataModel): LossRunForm {
    return this.formBuilder.group({
      notes: [lossRunRequestFormDataModel.notes.answer]
    });
  }

  private subscribeToFormChanges(): void {
    this.lossRunForm.valueChanges.subscribe((formValue) => {
      if (!!this.lossRunFormDataModel) {
        this.lossRunFormDataModel.notes.answer = formValue.notes;
      }
    });
  }

  public menuClicked(menuClickContext: MenuClickContext): void {
    this.menuClickContext = menuClickContext;
    if (menuClickContext != null && menuClickContext.page != EndorsementTypes.LossRun) {
      if (!this.lossRunForm || !this.fileUploadForm) {
        this.menuClickContext.page = EndorsementTypes.LossRun;
        return;
      }
      menuClickContext.currentPageIsValid = this.isPageValid();
      menuClickContext.currentPageDataChanged = this.isPageDataChanged();
      if (this.endorsementService.isMenuClickHeaderPage(menuClickContext)) {
        this.menuClickContext.page = EndorsementTypes.LossRun;
        return;
      }
      if (menuClickContext.currentPageIsValid && menuClickContext.currentPageDataChanged) {
        this.saveAndContinue();
      }
      this.endorsementService.handleUnsavedChangesOrNavigateToUrl(menuClickContext, this.endorsement);
    }
  }

  public isPageDataChanged(): boolean {
    return !_.isEqual(this.initialFormValue, this.lossRunForm.getRawValue()) || this.fileUploadForm.isFormDataChanged();
  }
  public isPageValid(): boolean {
    return this.lossRunForm.valid;
  }

  public onRollback(currentTempage: string): void { }

  public onBack(page: string): void { }

  public saveAndContinue(type = ""): void {
    this.isSubmitted = true;
    if (this.isPageValid()) {
      this.postQuestionAnswers().then(isSuccess => {
        if (isSuccess) {
          this.isSubmitted = false;
          let currrentEndorsementQuestionGroup : EndorsementQuestionGroup = this.endorsement.selectedEndorsementGroups.find(x => x.groupKey == EndorsementTypes.LossRun);
          currrentEndorsementQuestionGroup.hasAnswers = true;
          currrentEndorsementQuestionGroup.isValidPage = true;
          this.endorsement.userIsAt = '';
          if (type === "finalSubmit") {
              this.endorsementService.updateStatusToSubmitted(this.endorsement.endorsementId);
          }
          else if (type === "draft") {
            this.router.navigate(['/home'], { state: { data: 'endorsement' } });
          }
          // if menuClickContext is current page or not set, get and update next menuClickContext
          if (!this.menuClickContext || this.menuClickContext.page === EndorsementTypes.LossRun) {
            this.endorsementService.menuClickBehavior.next(this.endorsementService.getNextMenuClickContext(EndorsementTypes.LossRun));
          }
        }
      }).catch(error => {
        console.error(error);
        this.messageService.add({ key: 'toast', severity: 'error', summary: 'Loss Run Request', detail: error });
      })
    }
    else
      this.messageService.add({ key: 'toast', severity: 'error', summary: 'Loss Run Request', detail: this.toastValidation });
  }

  private async postQuestionAnswers(): Promise<boolean> {
    this.bindAnswersToQuestions();
    let payload = { "payloadData": this.endorsementQuestionGroup }
    return this.commonService.post('endorsement', 'postQuestionAnswers', payload).toPromise()
      .then(result => result)
      .catch(error => Promise.reject(error));
  }

  public bindAnswersToQuestions(): void {
    let questionAnswers: QuestionAnswer[] = this.lossRunFormDataModel.toQuestionAnswers();
    questionAnswers.push(...this.fileUploadForm.convertFileDocumentsToQuestionAnswer());
    this.endorsementQuestionGroup.questionAnswers = questionAnswers;
  }

  ngOnDestroy(): void {
    if (!!this.menuClickSubscription) {
      this.menuClickSubscription.unsubscribe();
    }
  }
}

class LossRunFormDataModel {
  public notes: emptyModal = new emptyModal();

  public static instantiateFrom(questionnaire: Questionnaire): LossRunFormDataModel {
    const dataModel = new LossRunFormDataModel();
    dataModel.notes = emptyModal.initializeFrom(questionnaire.findQuestion('notes'));
    return dataModel;
  }

  public toQuestionAnswers(): QuestionAnswer[] {
    return [this.notes];
  }
}
